import api from './api';
import { 
    Project, 
    TimeEntry, 
    PDI, 
    PDIGoal, 
    Reimbursement, 
    VacationRequest, 
    Task, 
    TimeEntryRequest 
} from '../types/collaborator';

// Projects
export const getUserProjects = async (): Promise<Project[]> => {
    const response = await api.get<Project[]>('/api/collaborator/projects');
    return response.data;
};

// Time Entries
export const getTimeEntries = async (startDate?: string, endDate?: string): Promise<TimeEntry[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const response = await api.get<TimeEntry[]>('/api/collaborator/time-entries', { params });
    return response.data;
};

export const createTimeEntry = async (data: TimeEntryRequest): Promise<void> => {
    await api.post('/api/collaborator/time-entries', data);
};

// PDI
export const getPDIs = async (): Promise<PDI[]> => {
    const response = await api.get<PDI[]>('/api/collaborator/pdi');
    return response.data;
};

export interface CreatePDIData {
    title: string;
    description: string;
    goals?: Omit<PDIGoal, 'id' | 'completed' | 'completed_at'>[];
}

export const createPDI = async (data: CreatePDIData): Promise<{ pdi_id: number }> => {
    const response = await api.post<{ pdi_id: number }>('/api/collaborator/pdi', data);
    return response.data;
};

// Marca a meta como concluída/pendente; devolve o PDI atualizado (o status do PDI acompanha as metas)
export const updatePDIGoalStatus = async (goalId: number, status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'): Promise<PDI> => {
    const response = await api.put<PDI>(`/api/collaborator/pdi/goals/${goalId}`, { status });
    return response.data;
};

// Reimbursements
export const getReimbursements = async (): Promise<Reimbursement[]> => {
    const response = await api.get<Reimbursement[]>('/api/collaborator/reimbursements');
    return response.data;
};

export interface CreateReimbursementData {
    description: string;
    amount: number;
    date: string; // data da despesa (YYYY-MM-DD)
    receipt?: File | null; // comprovante (PDF/JPG/PNG até 5 MB)
}

export const createReimbursement = async (data: CreateReimbursementData): Promise<void> => {
    // multipart: o comprovante vai junto com os dados, num único envio
    const form = new FormData();
    form.append('description', data.description);
    form.append('amount', String(data.amount));
    form.append('date', data.date);
    if (data.receipt) form.append('receipt', data.receipt);
    await api.post('/api/collaborator/reimbursements', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Vacation Requests
export const getVacationRequests = async (): Promise<VacationRequest[]> => {
    const response = await api.get<VacationRequest[]>('/api/collaborator/vacations');
    return response.data;
};

export interface CreateVacationRequestData {
    start_date: string;
    end_date: string;
    notes?: string;
}

export const createVacationRequest = async (data: CreateVacationRequestData): Promise<void> => {
    await api.post('/api/collaborator/vacations', data);
};

// Tasks
export const getTasks = async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/api/collaborator/tasks');
    return response.data;
};

export interface CreateTaskData {
    title: string;
    description?: string;
    due_date?: string;
}

export const createTask = async (data: CreateTaskData): Promise<void> => {
    await api.post('/api/collaborator/tasks', data);
};

export const completeTask = async (taskId: number): Promise<void> => {
    await api.post(`/api/collaborator/tasks/${taskId}/complete`);
};

class CollaboratorService {
    async getProjects() {
        const response = await api.get<Project[]>('/api/collaborator/projects');
        return response.data;
    }

    async getTimeEntries(projectId: number) {
        const response = await api.get<TimeEntry[]>(`/api/collaborator/projects/${projectId}/time-entries`);
        return response.data;
    }

    async createTimeEntry(projectId: number, data: TimeEntryRequest) {
        const response = await api.post<TimeEntry>(`/api/collaborator/projects/${projectId}/time-entries`, data);
        return response.data;
    }
}

export const collaboratorService = new CollaboratorService(); 